import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Input } from '@/components/ui/input';
import type { StatsRange } from '@/data/stats';

export type RangePreset = 'this_month' | 'last_month' | '90d' | 'custom';

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: '90d', label: '90 days' },
  { key: 'custom', label: 'Custom' },
];

/** End of a local calendar day, as ISO. */
function endOfDayIso(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function startOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toISOString();
}

/** The three presets resolve in the browser's local calendar. */
export function presetRange(preset: Exclude<RangePreset, 'custom'>): StatsRange {
  const now = new Date();
  if (preset === 'this_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      to: now.toISOString(),
    };
  }
  if (preset === 'last_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
      // Last millisecond of the previous month.
      to: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, -1).toISOString(),
    };
  }
  return {
    from: new Date(now.getTime() - 90 * 86_400_000).toISOString(),
    to: now.toISOString(),
  };
}

interface DateRangePickerProps {
  preset: RangePreset;
  onChange: (preset: RangePreset, range: StatsRange | null) => void;
}

/**
 * Preset pills + a custom two-date form. For 'custom', onChange fires with a
 * null range until both dates are picked and ordered — the page keeps
 * showing the previous data rather than issuing a broken query.
 */
export function DateRangePicker({ preset, onChange }: DateRangePickerProps) {
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  function emitCustom(from: string, to: string) {
    if (from && to && from <= to) {
      onChange('custom', { from: startOfDayIso(from), to: endOfDayIso(to) });
    } else {
      onChange('custom', null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex rounded-md border border-breaker-line-2 overflow-hidden" role="group" aria-label="Date range">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => {
              if (p.key === 'custom') emitCustom(customFrom, customTo);
              else onChange(p.key, presetRange(p.key));
            }}
            className={cn(
              'px-3 h-9 text-sm whitespace-nowrap transition-colors',
              'border-l border-breaker-line-2 first:border-l-0',
              preset === p.key
                ? 'bg-breaker-identity text-white'
                : 'bg-white text-breaker-ink-2 hover:bg-breaker-ground-2',
            )}
            aria-pressed={preset === p.key}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="From date"
            className="h-9 w-auto"
            value={customFrom}
            max={customTo || undefined}
            onChange={(e) => {
              setCustomFrom(e.target.value);
              emitCustom(e.target.value, customTo);
            }}
          />
          <span className="text-sm text-breaker-mute">to</span>
          <Input
            type="date"
            aria-label="To date"
            className="h-9 w-auto"
            value={customTo}
            min={customFrom || undefined}
            onChange={(e) => {
              setCustomTo(e.target.value);
              emitCustom(customFrom, e.target.value);
            }}
          />
        </div>
      )}
    </div>
  );
}
